
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import Layout from "./components/Layout";
import { Toaster } from "react-hot-toast";
import GlobalWhatsAppButton from "./components/GlobalWhatsAppButton";

// Eagerly loaded (always on screen or tiny)
import Home from "./pages/Home";

// Lazy-loaded pages — only downloaded when the user navigates to them
const Login               = lazy(() => import("./pages/login"));
const Signup              = lazy(() => import("./pages/Signup"));
const Dashboard           = lazy(() => import("./pages/Dashboard/index"));
const Addproduct          = lazy(() => import("./pages/Dashboard/add-product/app"));
const Catalog             = lazy(() => import("./pages/catalog"));
const Category            = lazy(() => import("./pages/category/index"));
const CategoryProducts    = lazy(() => import("./pages/category/[categoryName]/app"));
const ProductName         = lazy(() => import("./pages/[productName]/index"));
const ContactUs           = lazy(() => import("./pages/contact-us"));
const AboutUs             = lazy(() => import("./components/About-us"));
const NewArrival          = lazy(() => import("./components/new-arrival"));
const SearchResultsPage   = lazy(() => import("./components/SearchResults"));
const Cart                = lazy(() => import("./components/Cart"));
const Checkout            = lazy(() => import("./components/CheckoutPage"));
const NotFoundPage        = lazy(() => import("./components/NotFound"));
const Logout              = lazy(() => import("./components/Logout"));
const OrderManagement     = lazy(() => import("./components/OrderManagement"));
const DashboardContactUs  = lazy(() => import("./components/contact-us"));
const DashboardCatalog    = lazy(() => import("./components/dashboardCatalog"));
const ForgotPassword      = lazy(() => import("./pages/ForgotPassword"));
const ShippingSettings    = lazy(() => import("./pages/Dashboard/ShippingSettings"));
const CategoryManagement  = lazy(() => import("./pages/Dashboard/CategoryManagement"));
const HeroManagement      = lazy(() => import("./pages/Dashboard/HeroManagement"));
const DiscountManagement  = lazy(() => import("./pages/Dashboard/DiscountManagement"));
const BundleManagement    = lazy(() => import("./pages/Dashboard/BundleManagement"));
const BundlesPage         = lazy(() => import("./pages/Bundles"));
const FAQManagement       = lazy(() => import("./pages/Dashboard/FAQManagement"));
const BannaerManagement   = lazy(() => import("./pages/Dashboard/Bannermanagement"));

// AdminRoute - protects dashboard pages, redirects non-admin to /404.
function AdminRoute({ children }) {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setStatus('no-token'); return; }
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatus(res.data.isAdmin ? 'allowed' : 'denied');
      } catch {
        localStorage.removeItem('token');
        setStatus('no-token');
      }
    };
    checkAuth();
  }, []);

  if (status === 'loading') return null;
  if (status === 'no-token') return <Navigate to="/login" replace />;
  if (status === 'denied') return <Navigate to="/404" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function DynamicTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    const getTitle = () => {
      const base = "Arco";
      if (pathname === "/") return base;
      if (pathname === "/login") return `Login | ${base}`;
      if (pathname === "/signup") return `Signup | ${base}`;
      if (pathname === "/cart") return `Your Cart | ${base}`;
      if (pathname === "/checkout") return `Checkout | ${base}`;
      if (pathname === "/catalog") return `Product Catalog | ${base}`;
      if (pathname === "/category") return `Categories | ${base}`;
      if (pathname === "/about-us") return `About Us | ${base}`;
      if (pathname === "/contact-us") return `Contact Us | ${base}`;
      if (pathname === "/new-arrivals") return `New Arrivals | ${base}`;
      if (pathname === "/search") return `Search Results | ${base}`;
      if (pathname === "/bundles") return `Exclusive Bundles | ${base}`;
      if (pathname === "/forgot-password") return `Reset Password | ${base}`;
      if (pathname.startsWith("/dashboard")) {
        const subPage = pathname.split("/").pop();
        if (subPage === "dashboard") return `Admin Dashboard | ${base}`;
        const formattedSub = subPage.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        return `${formattedSub} | Admin | ${base}`;
      }
      if (pathname.startsWith("/category/")) {
        const catName = pathname.split("/")[2];
        const formattedCat = decodeURIComponent(catName).split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        return `${formattedCat} | ${base}`;
      }
      if (pathname.includes("/catalog/")) return `Product Details | ${base}`;
      if (pathname === "/404") return `Page Not Found | ${base}`;
      return base;
    };
    document.title = getTitle();
  }, [pathname]);

  return null;
}

// Minimal spinner shown while a lazy chunk loads
function PageLoader() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{
        width: 36, height: 36,
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #CC1B1B',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  return (
    <>
      <ScrollToTop />
      <DynamicTitle />
      <Toaster position="top-center" />
      <GlobalWhatsAppButton />
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
            <Route path="/dashboard/add-product" element={<AdminRoute><Addproduct /></AdminRoute>} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/catalog/:id" element={<ProductName />} />
            <Route path="/category" element={<Category />} />
            <Route path="/category/:categoryName" element={<CategoryProducts />} />
            <Route path="/category/:categoryName/:id" element={<ProductName />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/new-arrivals" element={<NewArrival />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/dashboard/order-management" element={<AdminRoute><OrderManagement /></AdminRoute>} />
            <Route path="/dashboard/contactus" element={<AdminRoute><DashboardContactUs /></AdminRoute>} />
            <Route path="/dashboard/catalog" element={<AdminRoute><DashboardCatalog /></AdminRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/dashboard/shipping" element={<AdminRoute><ShippingSettings /></AdminRoute>} />
            <Route path="/dashboard/categories" element={<AdminRoute><CategoryManagement /></AdminRoute>} />
            <Route path="/dashboard/hero" element={<AdminRoute><HeroManagement /></AdminRoute>} />
            <Route path="/dashboard/discounts" element={<AdminRoute><DiscountManagement /></AdminRoute>} />
            <Route path="/dashboard/bundles" element={<AdminRoute><BundleManagement /></AdminRoute>} />
            <Route path="/dashboard/faqs" element={<AdminRoute><FAQManagement /></AdminRoute>} />
            <Route path="/dashboard/banners" element={<AdminRoute><BannaerManagement /></AdminRoute>} />
            <Route path="/bundles" element={<BundlesPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </>
  );
}

export default App;
